/**
 * ACTA — la verificación de roster, el flujo de firmas y el cierre (§2.4, §8.3).
 *
 * ## QUIÉN FIRMA Y CON QUÉ SESIÓN
 *
 * `POST /sign` resuelve el firmante por la CREDENCIAL DEL REQUEST, no por un
 * campo del body — firmar es probar quién sos, no declararlo. Para que el
 * titular firme EN la laptop de la mesa:
 *
 *  - **credencial QR+PIN**: la mesa guarda el token al emitirla (Oficiales);
 *    acá se canjea (`matchLogin`) y la firma viaja con ESA sesión, con el PIN
 *    que el titular teclea en el momento (step-up, §4.3). Si el token no está
 *    en este navegador, la pantalla lo dice y ofrece la salida (reemitir o
 *    firmar desde el dispositivo del titular) en vez de fallar en silencio.
 *  - **cuenta del padrón**: firma con su contraseña sobre la sesión activa —
 *    vale cuando la sesión de la mesa es la del anotador designado, que es el
 *    caso normal del e-anotador.
 *
 * ## EL ORDEN ES EL DE LA INSTRUCCIÓN 4.10, Y SE MUESTRA ANTES DE CHOCARLO
 *
 * El anotador primero (regla 27.2.3.3: su firma congela el digest; es el único
 * corte DURO del motor), después capitanes → 2do árbitro → 1er árbitro →
 * challenge referee. La pantalla apaga los botones que el motor rechazaría y
 * marca FIRMA SIGUIENTE, así el orden se camina en vez de descubrirse a 422.
 * Una firma que no coincide con el digest congelado no se oculta: se marca en
 * rojo, porque mostrar eso ES la cadena de custodia (§8.3).
 */
import { useState } from 'react';
import type { TeamSide } from '@scout/core';
import { useT } from '../../i18n/use-t';
import type { MessageKey } from '../../i18n/index';
import { openHtmlInTab } from '../../browser';
import { Card, ErrorBox, Notice } from '../../components/States';
import { errorMessage } from '../../hooks';
import { defaultKeyValueStore } from '../../lib/settings';
import { roleKey, type MesaTabProps } from './MesaPage';
import {
  clearIssuedTokens,
  closingBoard,
  loadIssuedTokens,
  nextSigner,
  rosterBoard,
  type SignerSlot,
} from './mesa';

/** Copia de `DEFAULT_ORG_ID` (`@scout/core`): el tenant único del modo LAN. */
const DEFAULT_ORG = 'default';

const DELIVERIES = ['FEDERATION', 'TEAM_A', 'TEAM_B', 'ORGANISER', 'SUPERVISOR'] as const;
const OUTCOMES = ['NORMAL', 'DEFAULT', 'INCOMPLETE', 'WITHDRAWN'] as const;

interface Signing {
  slot: SignerSlot;
  stage: 'PREMATCH' | 'POSTMATCH';
}

function slotLabel(t: ReturnType<typeof useT>, slot: SignerSlot): string {
  const role = t(roleKey(slot.role));
  return slot.team !== null ? `${role} (${t(`mesa.team.${slot.team}` as MessageKey)})` : role;
}

export function ActaTab(props: MesaTabProps): JSX.Element {
  const { client, matchId, sheet, snapshot, identity, reload } = props;
  const t = useT();

  const [signing, setSigning] = useState<Signing | null>(null);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [outcome, setOutcome] = useState<(typeof OUTCOMES)[number]>('NORMAL');
  const [deliveries, setDeliveries] = useState<Set<string>>(
    () => new Set(['FEDERATION', 'TEAM_A', 'TEAM_B', 'ORGANISER']),
  );

  const doc = sheet.document;
  const closing = closingBoard(doc, sheet.assignments);
  const roster = rosterBoard(doc, sheet.assignments);
  const next = nextSigner(closing);
  const scorerSigned = closing.some((s) => s.role === 'scorer' && s.signature !== null);

  const startSign = (slot: SignerSlot, stage: 'PREMATCH' | 'POSTMATCH'): void => {
    setSigning({ slot, stage });
    setSecret('');
    setError(null);
    setNotice(null);
  };

  const sign = async (): Promise<void> => {
    if (signing === null || signing.slot.credential === null || secret === '') return;
    const { slot, stage } = signing;
    const credential = slot.credential;
    if (credential === null) return;
    setBusy(true);
    setError(null);
    try {
      const body = {
        secret,
        stage,
        ...(stage === 'PREMATCH' ? { document: 'ROSTER' as const } : {}),
        deviceId: 'mesa-portal',
      };
      if (credential.kind === 'credential') {
        const tokens = loadIssuedTokens(matchId, defaultKeyValueStore());
        const token = tokens[credential.subjectId];
        if (token === undefined) {
          setError(t('mesa.acta.needsToken'));
          return;
        }
        const login = await client.matchLogin({
          org: identity?.orgId ?? DEFAULT_ORG,
          matchId,
          credentialId: credential.subjectId,
          token,
        });
        await client.signSheet(matchId, body, { asToken: login.token });
      } else {
        await client.signSheet(matchId, body);
      }
      setNotice(t('mesa.acta.signedOk'));
      setSigning(null);
      setSecret('');
      reload();
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  const close = async (): Promise<void> => {
    if (!window.confirm(t('mesa.acta.confirmClose'))) return;
    setBusy(true);
    setError(null);
    try {
      await client.closeSheet(matchId, {
        outcome,
        deliveries: DELIVERIES.filter((d) => deliveries.has(d)),
      });
      // El acta se cerró: los tokens emitidos ya no tienen nada que firmar.
      clearIssuedTokens(matchId, defaultKeyValueStore());
      setNotice(t('mesa.acta.closedOk'));
      reload();
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  const printable = async (): Promise<void> => {
    setBusy(true);
    try {
      const file = await client.downloadScoresheetHtml(matchId);
      openHtmlInTab(file.body);
    } catch (err) {
      setError(errorMessage(err instanceof Error ? err : new Error(String(err))));
    } finally {
      setBusy(false);
    }
  };

  const signForm = (stage: 'PREMATCH' | 'POSTMATCH', slot: SignerSlot): JSX.Element | null => {
    if (signing === null || signing.stage !== stage) return null;
    const active = signing.slot;
    if (active.role !== slot.role || active.team !== slot.team) return null;
    const credential = active.credential;
    if (credential === null) return null;
    const isPin = credential.kind === 'credential';
    return (
      <div className="row" style={{ marginTop: 6, alignItems: 'flex-end' }}>
        <label className="field" style={{ flex: 1 }}>
          <span>
            {t('mesa.acta.signAs', {
              name: active.holderName ?? '',
              role: t(roleKey(active.role)),
            })}{' '}
            — {isPin ? t('mesa.acta.secretPin') : t('mesa.acta.secretPassword')}
          </span>
          <input
            type="password"
            autoComplete="off"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
          />
          {!isPin ? <span className="hint">{t('mesa.acta.accountHint')}</span> : null}
        </label>
        <button
          type="button"
          className="primary"
          disabled={busy || secret === ''}
          style={{ padding: '12px 22px' }}
          onClick={() => void sign()}
        >
          {busy ? '…' : t('mesa.acta.sign')}
        </button>
        <button type="button" className="small" onClick={() => setSigning(null)}>
          {t('common.cancel')}
        </button>
      </div>
    );
  };

  const slotRow = (stage: 'PREMATCH' | 'POSTMATCH', slot: SignerSlot): JSX.Element => {
    const isNext = stage === 'POSTMATCH' && next !== null && next.role === slot.role && next.team === slot.team;
    const blocked = stage === 'POSTMATCH' && slot.blockedByScorer;
    return (
      <li
        key={`${slot.role}-${slot.team ?? 'x'}`}
        className={`row${isNext && slot.signature === null ? ' next' : ''}`}
        style={{ flexWrap: 'wrap' }}
      >
        <span style={{ minWidth: 220 }}>
          <strong>{slotLabel(t, slot)}</strong>
          <br />
          <span className="hint">{slot.holderName ?? t('mesa.acta.noHolder')}</span>
        </span>
        <span className="spacer" />
        {slot.signature !== null ? (
          <span>
            <span className="chip ok">
              {t('mesa.acta.signedBy', { name: slot.signature.signerName })}
            </span>
            {!slot.signature.matchesFrozen ? (
              <span className="chip bad" style={{ marginLeft: 6 }}>
                {t('mesa.acta.digestMismatch')}
              </span>
            ) : null}
          </span>
        ) : (
          <span className="row" style={{ gap: 8 }}>
            {isNext ? <span className="chip warn">{t('mesa.acta.nextBadge')}</span> : null}
            <span className="chip muted">{t('mesa.acta.pendingSlot')}</span>
            <button
              type="button"
              disabled={busy || doc.closed || slot.holderName === null || blocked}
              title={blocked ? t('mesa.acta.scorerFirst') : undefined}
              style={{ padding: '10px 18px', fontWeight: 600 }}
              onClick={() => startSign(slot, stage)}
            >
              {t('mesa.acta.sign')}
            </button>
          </span>
        )}
        <div style={{ flexBasis: '100%' }}>{signForm(stage, slot)}</div>
      </li>
    );
  };

  return (
    <div>
      {error !== null ? <ErrorBox message={error} /> : null}
      {notice !== null ? <Notice message={notice} /> : null}

      <Card title={t('mesa.acta.rosterTitle')}>
        <p className="hint">{t('mesa.acta.rosterExplain')}</p>
        <div className="grid-2">
          {(['home', 'away'] as TeamSide[]).map((team) => (
            <section key={team}>
              <div className="row" style={{ marginBottom: 6 }}>
                <strong>
                  {snapshot?.meta[team].name ?? t(`mesa.team.${team}` as MessageKey)}
                </strong>
                <span className="chip muted">
                  {t('mesa.acta.players', { count: snapshot?.meta[team].players.length ?? 0 })}
                </span>
                <span className="spacer" />
                <span className={`chip ${doc.rosterLocked[team] ? 'ok' : 'warn'}`}>
                  {doc.rosterLocked[team]
                    ? t('mesa.acta.rosterLocked')
                    : t('mesa.acta.rosterOpen')}
                </span>
              </div>
              <ul className="sign-list">
                {roster[team].map((slot) => slotRow('PREMATCH', slot))}
              </ul>
            </section>
          ))}
        </div>
      </Card>

      <Card title={t('mesa.acta.signaturesTitle')}>
        <p className="hint">{t('mesa.acta.scorerFirst')}</p>
        {doc.frozenDigest !== null ? (
          <p className="hint" style={{ fontFamily: 'monospace' }}>
            {t('mesa.acta.frozenDigest', { digest: doc.frozenDigest })}
          </p>
        ) : null}
        <ul className="sign-list">
          {closing.map((slot) => slotRow('POSTMATCH', slot))}
        </ul>
      </Card>

      <Card title={t('mesa.acta.closeTitle')}>
        <p className="hint">{t('mesa.acta.closeExplain')}</p>
        {doc.closed ? (
          <div>
            <p className="chip ok">{t('mesa.acta.closedOk')}</p>
            {doc.closedDigest !== null ? (
              <p className="hint" style={{ fontFamily: 'monospace' }}>
                {t('mesa.acta.closedDigest', { digest: doc.closedDigest })}
              </p>
            ) : null}
            <button type="button" disabled={busy} onClick={() => void printable()}>
              {t('mesa.acta.printable')}
            </button>
          </div>
        ) : (
          <div>
            <div className="row" style={{ alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <label className="field" style={{ minWidth: 240 }}>
                <span>{t('mesa.acta.outcomeLabel')}</span>
                <select
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as (typeof OUTCOMES)[number])}
                >
                  {OUTCOMES.map((o) => (
                    <option key={o} value={o}>
                      {t(`mesa.acta.outcomes.${o}` as MessageKey)}
                    </option>
                  ))}
                </select>
              </label>
              <fieldset className="field" style={{ border: 'none', margin: 0, padding: 0 }}>
                <span className="hint">{t('mesa.acta.deliveriesLabel')}</span>
                <div className="row" style={{ flexWrap: 'wrap' }}>
                  {DELIVERIES.map((d) => (
                    <label key={d} className="row" style={{ gap: 4 }}>
                      <input
                        type="checkbox"
                        checked={deliveries.has(d)}
                        onChange={(e) => {
                          setDeliveries((prev) => {
                            const nextSet = new Set(prev);
                            if (e.target.checked) nextSet.add(d);
                            else nextSet.delete(d);
                            return nextSet;
                          });
                        }}
                      />
                      <span>{t(`mesa.acta.deliveries.${d}` as MessageKey)}</span>
                    </label>
                  ))}
                </div>
              </fieldset>
            </div>
            {!scorerSigned ? <p className="hint">{t('mesa.acta.closeUnsigned')}</p> : null}
            <div className="row" style={{ marginTop: 10 }}>
              <button type="button" disabled={busy} onClick={() => void printable()}>
                {t('mesa.acta.printable')}
              </button>
              <span className="spacer" />
              <button
                type="button"
                className="danger big"
                disabled={busy || !scorerSigned}
                onClick={() => void close()}
              >
                {busy ? '…' : t('mesa.acta.close')}
              </button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
